import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AppointmentBooking } from "@/components/appointment-booking";
import { AppointmentCard } from "@/components/appointment-card";
import { PageHeader } from "@/components/page-header";
import { PageLoading } from "@/components/page-loading";
import { PageShell } from "@/components/page-shell";
import { FormError } from "@/components/ui/form-error";
import { useConfirm } from "@/components/use-confirm";
import {
  appointmentsQueryOptions,
  ensureConsumerQueryData,
  locationsQueryOptions,
  useDeleteAppointmentMutation,
} from "@/lib/data";
import { requireSession } from "@/lib/session";

export const Route = createFileRoute("/appointments")({
  component: AppointmentsPage,
  beforeLoad: requireSession,
  loader: () =>
    Promise.all([
      ensureConsumerQueryData(appointmentsQueryOptions),
      ensureConsumerQueryData(locationsQueryOptions),
    ]),
});

function AppointmentsPage() {
  const confirm = useConfirm();
  const { data: apptData, isLoading } = useQuery(appointmentsQueryOptions);
  const deleteAppointment = useDeleteAppointmentMutation();

  const appointments = apptData?.appointments ?? [];

  async function handleCancel(id: string) {
    const ok = await confirm({
      title: "Cancel this appointment?",
      description: "The dealer will be notified.",
      confirmLabel: "Cancel appointment",
      cancelLabel: "Keep it",
      destructive: true,
    });
    if (ok) deleteAppointment.mutate(id);
  }

  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Appointments"
        description="Pick a day and time that works best for you."
      />

      {/* The mutation clears its own error when the next attempt starts. */}
      {deleteAppointment.isError && (
        <div className="mb-4">
          <FormError message="Couldn't cancel that appointment. Please try again." />
        </div>
      )}

      {appointments.length > 0 && (
        <div className="mb-6 space-y-3">
          {appointments.map((appt) => (
            <AppointmentCard
              key={appt.id}
              appointment={appt}
              onCancel={handleCancel}
              cancelling={deleteAppointment.isPending}
            />
          ))}
        </div>
      )}

      <AppointmentBooking />
    </PageShell>
  );
}
